"use client";

import type { InvestmentCurveData } from "@atlas/computation";
import { useMemo } from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { formatCurrency } from "../lib/format";

// -- Constants ---

const MAX_BENEFIT_STREAMS = 3;
const OTHER_BENEFIT_COLOR = "#a3a3a3";

// -- Props ---

interface InvestmentCurveChartProps {
  data: InvestmentCurveData;
  contractTermMonths: number;
  horizon: 12 | "contract";
  onHorizonChange: (h: 12 | "contract") => void;
}

// -- Top-N benefit grouping --

interface GroupedBenefitMeta {
  familyId: string;
  label: string;
  color: string;
}

function groupBenefits(data: InvestmentCurveData): {
  topMetas: GroupedBenefitMeta[];
  hasOther: boolean;
  getMonthValues: (
    month: InvestmentCurveData["months"][number],
  ) => Record<string, number>;
} {
  if (data.benefitMetas.length <= MAX_BENEFIT_STREAMS) {
    return {
      topMetas: data.benefitMetas,
      hasOther: false,
      getMonthValues: (m) => {
        const result: Record<string, number> = {};
        for (const bm of data.benefitMetas) {
          result[`benefit_${bm.familyId}`] =
            m.benefits.byStream[bm.familyId] ?? 0;
        }
        return result;
      },
    };
  }

  // Rank benefit streams by total value across all months
  const totals: Record<string, number> = {};
  for (const bm of data.benefitMetas) {
    totals[bm.familyId] = 0;
    for (const m of data.months) {
      totals[bm.familyId] =
        (totals[bm.familyId] ?? 0) + (m.benefits.byStream[bm.familyId] ?? 0);
    }
  }

  const sorted = [...data.benefitMetas].sort(
    (a, b) => (totals[b.familyId] ?? 0) - (totals[a.familyId] ?? 0),
  );
  const top = sorted.slice(0, MAX_BENEFIT_STREAMS);
  const topIds = new Set(top.map((t) => t.familyId));

  const topMetas: GroupedBenefitMeta[] = [
    ...top,
    {
      familyId: "_other_benefits",
      label: "Other Benefits",
      color: OTHER_BENEFIT_COLOR,
    },
  ];

  return {
    topMetas,
    hasOther: true,
    getMonthValues: (m) => {
      const result: Record<string, number> = {};
      let other = 0;
      for (const bm of data.benefitMetas) {
        const val = m.benefits.byStream[bm.familyId] ?? 0;
        if (topIds.has(bm.familyId)) {
          result[`benefit_${bm.familyId}`] = val;
        } else {
          other += val;
        }
      }
      result.benefit__other_benefits = other;
      return result;
    },
  };
}

// -- Tooltip --

function CurveTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload: Record<string, unknown> }>;
}) {
  if (!active || !payload?.[0]) return null;
  const row = payload[0].payload;
  const month = row._month as number;
  const phase = row._phase as string;
  const costMetas = row._costMetas as InvestmentCurveData["costMetas"];
  const displayBenefits = row._displayBenefits as GroupedBenefitMeta[];
  const netMonthly = row._netMonthly as number;
  const cumulativeNet = row._cumulativeNet as number;

  const phaseLabel =
    phase === "implementation"
      ? "Implementation"
      : phase === "ramp"
        ? "Ramp-Up"
        : "Steady State";

  return (
    <div className="bg-(--bg-white) rounded-lg border border-(--border-subtle) shadow-lg p-3 text-xs min-w-[200px]">
      <p className="font-semibold text-(--fg-primary) border-b border-(--border-subtle) pb-1 mb-1">
        Month {month}{" "}
        <span className="text-(--fg-muted) font-normal">{phaseLabel}</span>
      </p>
      {displayBenefits.map((bm) => {
        const val = row[`benefit_${bm.familyId}`] as number;
        if (val === 0) return null;
        return (
          <div key={bm.familyId} className="flex justify-between">
            <span style={{ color: bm.color }}>{bm.label}</span>
            <span className="text-(--fg-primary)">{formatCurrency(val)}</span>
          </div>
        );
      })}
      <div className="border-t border-(--border-subtle) mt-1 pt-1">
        {costMetas.map((cm: InvestmentCurveData["costMetas"][number]) => {
          const val = row[`cost_${cm.familyId}`] as number;
          if (val === 0) return null;
          return (
            <div key={cm.familyId} className="flex justify-between">
              <span style={{ color: cm.color }}>{cm.label}</span>
              <span className="text-(--status-error,#dc2626)">
                {formatCurrency(val)}
              </span>
            </div>
          );
        })}
      </div>
      <div className="border-t border-(--border-subtle) mt-1 pt-1 space-y-0.5">
        <div className="flex justify-between font-medium">
          <span className="text-(--fg-secondary)">Net Monthly</span>
          <span
            className={
              netMonthly >= 0
                ? "text-(--status-success-text,#047857)"
                : "text-(--status-error,#dc2626)"
            }
          >
            {formatCurrency(netMonthly, { signed: true })}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-(--fg-secondary)">Cumulative</span>
          <span
            className={
              cumulativeNet >= 0
                ? "text-(--status-success-text,#047857)"
                : "text-(--status-error,#dc2626)"
            }
          >
            {formatCurrency(cumulativeNet, { signed: true })}
          </span>
        </div>
      </div>
    </div>
  );
}

// -- Component ---

export function InvestmentCurveChart({
  data,
  contractTermMonths,
  horizon,
  onHorizonChange,
}: InvestmentCurveChartProps) {
  const visibleMonths = horizon === 12 ? 12 : contractTermMonths;

  const { topMetas, getMonthValues } = useMemo(
    () => groupBenefits(data),
    [data],
  );

  // Transform data for Recharts
  const chartData = useMemo(() => {
    return data.months.map((m: InvestmentCurveData["months"][number]) => {
      const benefitValues = getMonthValues(m);
      const row: Record<string, unknown> = {
        month: `M${m.month}`,
        _month: m.month,
        _phase: m.phase,
        _costMetas: data.costMetas,
        _displayBenefits: topMetas,
        _netMonthly: m.netMonthly,
        _cumulativeNet: m.cumulativeNet,
        cumulative: m.cumulativeNet,
        ...benefitValues,
      };
      // Costs as negative values for downward bars
      for (const cm of data.costMetas) {
        row[`cost_${cm.familyId}`] = -(m.costs.byFamily[cm.familyId] ?? 0);
      }
      return row;
    });
  }, [data, topMetas, getMonthValues]);

  const summary = horizon === 12 ? data.summary.year1 : data.summary.contract;

  return (
    <div className="bg-surface rounded-lg border border-border p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold text-foreground">
            Investment Curve
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Monthly cost outlay vs. benefit realization
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-md border border-border text-xs">
            <button
              type="button"
              className={`px-3 py-1 rounded-l-md ${horizon === 12 ? "bg-blue-600 text-white" : "text-muted-foreground hover:bg-surface-hover"}`}
              onClick={() => onHorizonChange(12)}
            >
              12 Months
            </button>
            <button
              type="button"
              className={`px-3 py-1 rounded-r-md ${horizon === "contract" ? "bg-blue-600 text-white" : "text-muted-foreground hover:bg-surface-hover"}`}
              onClick={() => onHorizonChange("contract")}
            >
              Full Contract ({contractTermMonths} mo)
            </button>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-x-3 gap-y-1 mb-3 text-[10px]">
        {data.costMetas.map((cm: InvestmentCurveData["costMetas"][number]) => (
          <span key={cm.familyId} className="flex items-center gap-1">
            <span
              className="inline-block h-2.5 w-2.5 rounded"
              style={{ backgroundColor: cm.color }}
            />
            {cm.label}
          </span>
        ))}
        <span className="text-dim-foreground">|</span>
        {topMetas.map((bm) => (
          <span key={bm.familyId} className="flex items-center gap-1">
            <span
              className="inline-block h-2.5 w-2.5 rounded"
              style={{ backgroundColor: bm.color }}
            />
            {bm.label}
          </span>
        ))}
        <span className="flex items-center gap-1 border-l border-border pl-2">
          <span className="inline-block h-0 w-4 border-t-2 border-dashed border-dim-foreground" />
          Net Cumulative
        </span>
      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={320}>
        <ComposedChart
          data={chartData.slice(0, visibleMonths)}
          margin={{ top: 28, right: 10, left: 10, bottom: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
          <XAxis
            dataKey="month"
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 11, fill: "#9ca3af" }}
          />

          {/* Left Y-axis: monthly bars */}
          <YAxis
            yAxisId="bars"
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 11, fill: "#9ca3af" }}
            tickFormatter={(v: number) => formatCurrency(v)}
            width={65}
          />

          {/* Right Y-axis: cumulative line */}
          <YAxis
            yAxisId="cumulative"
            orientation="right"
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 11, fill: "#94a3b8" }}
            tickFormatter={(v: number) => formatCurrency(v)}
            width={65}
          />

          <Tooltip content={<CurveTooltip />} />

          {/* Zero line */}
          <ReferenceLine
            yAxisId="bars"
            y={0}
            stroke="#94a3b8"
            strokeDasharray="4 4"
            strokeWidth={1}
          />

          {/* Payback annotation -- in-range */}
          {data.paybackMonth != null && data.paybackMonth <= visibleMonths && (
            <ReferenceLine
              yAxisId="bars"
              x={`M${Math.ceil(data.paybackMonth)}`}
              stroke="#3b82f6"
              strokeDasharray="4 4"
              strokeWidth={1.5}
              label={{
                value: `Payback: ${data.paybackMonth.toFixed(1)} mo`,
                position: "top",
                fontSize: 10,
                fill: "#3b82f6",
              }}
            />
          )}

          {/* Payback annotation -- projected beyond visible horizon */}
          {data.paybackMonth != null && data.paybackMonth > visibleMonths && (
            <ReferenceLine
              yAxisId="bars"
              x={`M${visibleMonths}`}
              stroke="#3b82f6"
              strokeDasharray="2 3"
              strokeWidth={1}
              label={{
                value: `Payback: ~${data.paybackMonth.toFixed(1)} mo (projected)`,
                position: "top",
                fontSize: 10,
                fill: "#3b82f680",
              }}
            />
          )}

          {/* Cost bars (negative, stacked) */}
          {data.costMetas.map(
            (cm: InvestmentCurveData["costMetas"][number], i: number) => (
              <Bar
                key={cm.familyId}
                yAxisId="bars"
                dataKey={`cost_${cm.familyId}`}
                stackId="costs"
                fill={cm.color}
                fillOpacity={0.85}
                radius={
                  i === data.costMetas.length - 1 ? [0, 0, 2, 2] : undefined
                }
              />
            ),
          )}

          {/* Benefit bars (positive, stacked) -- top N + "Other" */}
          {topMetas.map((bm, i) => (
            <Bar
              key={bm.familyId}
              yAxisId="bars"
              dataKey={`benefit_${bm.familyId}`}
              stackId="benefits"
              fill={bm.color}
              fillOpacity={0.85}
              radius={i === 0 ? [2, 2, 0, 0] : undefined}
            />
          ))}

          {/* Cumulative net line (right axis) */}
          <Line
            yAxisId="cumulative"
            type="monotone"
            dataKey="cumulative"
            stroke="#64748b"
            strokeWidth={2}
            strokeDasharray="6 4"
            dot={false}
          />
        </ComposedChart>
      </ResponsiveContainer>

      {/* Summary stats */}
      <div className="flex gap-4 mt-4 pt-4 border-t border-border">
        <Stat
          label="Payback Month"
          value={
            data.paybackMonth != null ? data.paybackMonth.toFixed(1) : "Never"
          }
          color="text-info-foreground"
        />
        <Stat
          label={`${horizon === 12 ? "Yr 1" : "Contract"} Total Cost`}
          value={formatCurrency(summary.totalCost)}
          color="text-danger-foreground"
        />
        <Stat
          label={`${horizon === 12 ? "Yr 1" : "Contract"} Total Benefit`}
          value={formatCurrency(summary.totalBenefit)}
          color="text-success-muted"
        />
        <Stat
          label={`${horizon === 12 ? "Yr 1" : "Contract"} Net`}
          value={formatCurrency(summary.net, { signed: true })}
          color={
            summary.net >= 0 ? "text-success-muted" : "text-danger-foreground"
          }
        />
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color: string;
}) {
  return (
    <div className="flex-1 text-center">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <p className={`text-lg font-bold mt-0.5 ${color}`}>{value}</p>
    </div>
  );
}
